import { Button, ButtonIcon, View } from '@gluestack-ui/themed'
import { DrawerScreenProps } from '@react-navigation/drawer'
import React, { FunctionComponent, useEffect } from 'react'
import { AppState, Linking, Platform } from 'react-native'
import RNBootSplash from 'react-native-bootsplash'
import Ionicons from 'react-native-vector-icons/Ionicons'
import { useSelector } from 'react-redux'
import { HomeComponentName } from '../../api/Home'
import { GridPageList } from '../../components/GridPageList'
import { MyCitysHome } from '../../components/MyCitysHome/MyCitysHome.component'
import { NotificationButton } from '../../components/NotificationButton'
import { Page } from '../../components/Page'
import { env } from '../../environment'
import { i18n } from '../../lib/i18n'
import { ActionViewParamsType, RoutesPromotion } from '../../navigation/Routes'
import { apiTokenSelector } from '../../redux/authentication/selectors'
import { homeComponentNameSelector, homeTitleImageSelector, homeTitleSelector, promotionSelector } from '../../redux/home/selector'
import { hasNewNotificationSelector, notificationsEnabledSelector } from '../../redux/notification/selector'
import { RootState } from '../../redux/reducers'
import { deepLinkNavigate } from '../../services/navigation'
import { readNotificationPermission, removeNotificationListeners } from '../../services/notification'
import { theme } from '../../theme'
import { UserAuthenticationButton } from './components/UserAuthenticationButton/UserAuthenticationButton.component'

const getComponent = (componentName: HomeComponentName) => {
  switch (componentName) {
    case 'favorites':
      return <View />
    case 'news':
      RNBootSplash.hide()
      return <MyCitysHome />
    default:
      return <GridPageList isFavoriesEnabled={env().featureFlags.isFavoritesEnabled} />
  }
}

export const Home: FunctionComponent<DrawerScreenProps<ActionViewParamsType, 'Home'>> = props => {
  const title = useSelector(homeTitleSelector)
  const titleHome = title || i18n.t('home.title')
  const titleImage = useSelector(homeTitleImageSelector)
  const promotion = useSelector(promotionSelector)
  const authtoken = useSelector((state: RootState) => apiTokenSelector(state))
  const isAuthenticated = !!authtoken

  const permission = useSelector((state: RootState) => notificationsEnabledSelector(state))
  const hasNewNotification = useSelector((state: RootState) => hasNewNotificationSelector(state))
  const HomeComponentName = useSelector(homeComponentNameSelector)

  const goToPromotion = () => {
    if (promotion) {
      setTimeout(
        () => {
          props.navigation.navigate({ name: RoutesPromotion.Promotion, params: {} })
        },
        Platform.OS === 'web' ? 250 : 0,
      )
    }
  }

  useEffect(() => {
    // Deep links natifs (Universal Links iOS / App Links Android) : cold start + foreground.
    // Remplace @react-native-firebase/dynamic-links (Firebase Dynamic Links, service Google arrêté le 25/08/2025).
    if (Platform.OS === 'web') return
    Linking.getInitialURL().then(url => {
      if (url) {
        deepLinkNavigate({ url })
      }
    })
    const subscription = Linking.addEventListener('url', deepLinkNavigate)
    return () => subscription.remove()
  }, [])

  useEffect(
    () => () => {
      if (isAuthenticated) removeNotificationListeners()
    },
    [isAuthenticated],
  )

  useEffect(() => {
    // Réglages OS modifiés en dehors de l'app : on relit la permission au retour au premier plan, dans les deux sens.
    if (Platform.OS === 'web' || !isAuthenticated) {
      return
    }
    let previousAppState = AppState.currentState
    const subscription = AppState.addEventListener('change', nextAppState => {
      if (/inactive|background/.test(previousAppState) && nextAppState === 'active') {
        readNotificationPermission()
      }
      previousAppState = nextAppState
    })
    return () => subscription.remove()
  }, [isAuthenticated])

  useEffect(goToPromotion, [promotion])

  const notificationProps = {
    enabled: Platform.OS === 'web' ? true : permission,
    hasNewNotification,
  }

  return (
    <Page
      title={titleHome}
      titleImage={titleImage}
      left={
        <Button variant="link" onPress={props.navigation.toggleDrawer}>
          <ButtonIcon as={() => <Ionicons name="menu" size={28} color={theme.colors.white} />} />
        </Button>
      }
      right={
        <>
          <NotificationButton {...notificationProps} />
          <UserAuthenticationButton />
        </>
      }
    >
      {getComponent(HomeComponentName)}
    </Page>
  )
}
